import AppBar from '../../components/shared/AppBar/AppBar';
import NavigationUtils from '../../utils/navigation.utils';
import { useEffect, useRef, useState } from 'preact/hooks';
import useTranslation from '../../localization/i18n.context';
import style from './P2PContactListPage.css';
import P2PContactPageSkeleton from '../../components/PeerToPeer/P2PContactLIstSkeleton/P2PContactPageSkeleton';
import { PermissionsEvent } from '../../sdk/permissions.event';
import { ContactsEvent } from '../../sdk/contacts.event';
import ContactsService from '../../services/contacts.service';
import P2PContactPageView from '../../components/PeerToPeer/P2PContactPageView/P2PContactPageView';
import { EducationalOnboardingInterceptor } from '../EducationalOnboardingPage/EducationalOnboardingInterceptor';
import { EducationalOnboardingType } from '../EducationalOnboardingPage/EducationalOnboardingProvider';
import useConfig from '../../config/config.context';
import { ConsoleEvent } from '../../sdk/console.event';
import usePermissionCheck from '../../hooks/permission.listener.hook';

const ContactsEventTimeoutInMs = 20000;

export default function P2PContactListPage({ cardUid }) {
  const { t } = useTranslation();
  const backRef = useRef(null);
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [timeoutId, setTimeoutId] = useState(null);
  const { config, updateStorage } = useConfig();

  function onPermissionResponse(data, times) {
    setIsLoading(true);
    const { status } = data;

    if (status === PermissionsEvent.Status.DENIED) {
      setPermissionGranted(false);
      setIsLoading(false);
      return;
    }

    if (
      status === PermissionsEvent.Status.ASK_AGAIN &&
      times < PermissionsEvent.PermissionMaxRequestTimes
    ) {
      return PermissionsEvent.check(
        PermissionsEvent.Resources.CONTACTS,
        PermissionsEvent.Action.ASK,
      );
    }

    setPermissionGranted(true);

    ContactsEvent.requestContacts(0, 100);

    const id = setTimeout(() => {
      ConsoleEvent.log('Timeout: No response from ContactsEvent');
      setIsLoading(false);
    }, ContactsEventTimeoutInMs);

    setTimeoutId(id);
  }

  async function onDeviceContactsFetch(event) {
    const { eventData } = event.detail;

    if (timeoutId != null) {
      clearTimeout(timeoutId);
    }

    const phones = eventData.data.map(contact =>
      contact.phone.replace('+', ''),
    );

    try {
      if (phones.length > 0) {
        const response = await ContactsService.getCaaSContacts(phones);

        const newContacts = [];

        for (const connectContact of response.data) {
          const deviceContact = eventData.data.find(
            c => c.phone.replace('+', '') === connectContact.phoneNumber,
          );

          if (deviceContact == null) {
            console.error(`Device contact not found`);
            continue;
          }

          newContacts.push({
            id: connectContact.id,
            phone: connectContact.phoneNumber,
            name: deviceContact.name,
          });
        }

        setContacts(prevState => [...prevState, ...newContacts]);
      }
    } catch (e) {
      console.error(`Could not fetch CaaS contacts`, e);
      return NavigationUtils.navigate(NavigationUtils.Routes.TRY_AGAIN);
    }

    if (eventData.pagination.hasNext) {
      ContactsEvent.requestContacts(eventData.pagination.page + 1, 100);
    } else {
      setIsLoading(false);
    }
  }

  usePermissionCheck(onPermissionResponse, PermissionsEvent.Resources.CONTACTS);

  useEffect(() => {
    const destinationPath = `${NavigationUtils.Routes.P2P_CONTACT_LIST.replace(
      ':cardUid',
      cardUid,
    )}`;

    //if there is an EO to show, the P2P flow is paused until EO has been finished
    if (
      EducationalOnboardingInterceptor.interceptURL(
        EducationalOnboardingType.SendMoney,
        destinationPath,
        config,
        updateStorage,
      )
    ) {
      return;
    }

    PermissionsEvent.check(
      PermissionsEvent.Resources.CONTACTS,
      PermissionsEvent.Action.ASK,
    );

    document.addEventListener(
      window.__CAAS__.events.getItems,
      onDeviceContactsFetch,
    );

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      document.removeEventListener(
        window.__CAAS__.events.getItems,
        onDeviceContactsFetch,
      );
    };
  }, []);

  function onGoToSettingsButtonClick() {
    PermissionsEvent.check(
      PermissionsEvent.Resources.CONTACTS,
      PermissionsEvent.Action.OPEN_SETTINGS,
    );
  }

  return (
    <>
      <AppBar
        onBackClick={() => {
          NavigationUtils.back();
        }}
        onCloseClick={() => {
          NavigationUtils.endFlow();
        }}
        title={t('Send money')}
        backRef={backRef}
        className="fixed-bar"
      />
      <div className={style.container}>
        {isLoading ? (
          <P2PContactPageSkeleton className={style.skeleton} />
        ) : (
          <P2PContactPageView
            className={style.content}
            contacts={contacts}
            onGoToSettingsButtonClick={onGoToSettingsButtonClick}
            permissionGranted={permissionGranted}
            cardUid={cardUid}
          />
        )}
      </div>
    </>
  );
}
